import { apiGet, apiPost } from './utils';
import type { FHRequest, PagedResponse, PendingResponse } from '../types';

export const mentorApi = {
  /**
   * Get the pending response awaiting mentor approval
   */
  async getPendingResponse(requestKey: string): Promise<PendingResponse> {
    return apiGet<PendingResponse>(`/request/mentor/pending-response/${requestKey}`);
  },

  /**
   * Get tasks awaiting mentor approval
   */
  async getQueue(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/mentor/queue', { cursor });
  },

  /**
   * Get the oldest unclaimed task in the mentor queue (FIFO).
   * Returns the task if one exists, or null if the queue is empty.
   */
  async getNextTask(): Promise<FHRequest | null> {
    const result = await apiGet<PagedResponse<FHRequest>>('/request/mentor/queue', {
      order: 'asc',
    });
    return result.items.length > 0 ? result.items[0] : null;
  },

  /**
   * Claim a task for mentoring
   */
  async claim(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>(`/request/mentor/claim/${requestKey}`);
  },

  /**
   * Release a mentor claim
   */
  async unclaim(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>(`/request/mentor/unclaim/${requestKey}`);
  },

  /**
   * Approve assistant's response
   */
  async approve(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>(`/request/mentor/approve/${requestKey}`);
  },

  /**
   * Deny assistant's response with feedback
   */
  async deny(requestKey: string, reason: string): Promise<FHRequest> {
    return apiPost<FHRequest>(`/request/mentor/deny/${requestKey}`, { reason });
  },

  /**
   * Recall a submitted response before a mentor claims it
   * Allows the assistant to edit their response
   * Returns the request and the recalled response body for editing
   */
  async recall(requestKey: string): Promise<RecallResponse> {
    return apiPost<RecallResponse>(`/request/mentor/recall/${requestKey}`);
  },
};

export interface RecallResponse {
  request: FHRequest;
  recalled_response: string | null;
  recalled_suggested_status: number | null;
  recalled_delay_time: string | null;
}
